import { useState, useEffect } from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Card, CardContent } from '../../../ui/card';
import { Loader2 } from 'lucide-react';
import { IconUploadField } from '../components/IconUploadField';
import { useUpdateCuratedIntegration } from '../../../../api/curated-integrations.api';
import type {
  CuratedIntegrationListItemDto,
  UpdateCuratedIntegrationDto,
} from '../../../../api/curated-integrations.types';
import { toast } from 'sonner';

interface BasicsTabProps {
  /** The integration being edited. */
  integration: CuratedIntegrationListItemDto;
  /** Called after a successful save so the parent can refresh the integration data. */
  onSaved: () => void;
}

interface FormState {
  name: string;
  displayName: string;
  description: string;
  category: string;
  mcpUrl: string;
  iconUrl: string;
}

interface FieldErrors {
  name?: string;
  mcpUrl?: string;
}

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim()) {
    errors.name = 'Name ist erforderlich.';
  }
  if (!form.mcpUrl.trim()) {
    errors.mcpUrl = 'MCP-URL ist erforderlich.';
  } else {
    try {
      const url = new URL(form.mcpUrl.trim());
      if (url.protocol !== 'https:') {
        errors.mcpUrl = 'URL muss mit https:// beginnen.';
      }
    } catch {
      errors.mcpUrl = 'Ungültige URL.';
    }
  }
  return errors;
}

/**
 * Tab "Basics" der Integration-Detail-Page.
 * Zeigt ein Formular für name, displayName, description, category, mcpUrl + Icon-Upload.
 */
export function BasicsTab({ integration, onSaved }: BasicsTabProps) {
  const [form, setForm] = useState<FormState>({
    name: integration.name,
    displayName: integration.displayName ?? '',
    description: integration.description ?? '',
    category: integration.category ?? '',
    mcpUrl: '',
    iconUrl: integration.iconUrl ?? '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { mutateAsync, isLoading, error } = useUpdateCuratedIntegration();

  // Sync form if integration prop changes (e.g. after refetch)
  useEffect(() => {
    setForm({
      name: integration.name,
      displayName: integration.displayName ?? '',
      description: integration.description ?? '',
      category: integration.category ?? '',
      mcpUrl: '',
      iconUrl: integration.iconUrl ?? '',
    });
  }, [integration]);

  const handleChange =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (fieldErrors[field as keyof FieldErrors]) {
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validate(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const dto: UpdateCuratedIntegrationDto = {
      name: form.name.trim(),
      displayName: form.displayName.trim() || undefined,
      description: form.description.trim() || undefined,
      category: form.category.trim() || undefined,
      ...(form.mcpUrl.trim() ? { mcpUrl: form.mcpUrl.trim() } : {}),
    };

    try {
      await mutateAsync({ id: integration.id, dto });
      toast.success('Änderungen gespeichert.');
      onSaved();
    } catch {
      // error rendered below
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
        >
          {error.message}
        </div>
      )}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* name */}
            <div className="space-y-1.5">
              <Label htmlFor="basics-name">
                Name <span aria-hidden="true" className="text-red-500">*</span>
              </Label>
              <Input
                id="basics-name"
                value={form.name}
                onChange={handleChange('name')}
                aria-required="true"
                aria-invalid={!!fieldErrors.name}
                aria-describedby={fieldErrors.name ? 'basics-name-error' : undefined}
              />
              {fieldErrors.name && (
                <p id="basics-name-error" role="alert" className="text-xs text-red-600">
                  {fieldErrors.name}
                </p>
              )}
            </div>

            {/* displayName */}
            <div className="space-y-1.5">
              <Label htmlFor="basics-display-name">Anzeigename</Label>
              <Input
                id="basics-display-name"
                placeholder="Name im Kundenportal"
                value={form.displayName}
                onChange={handleChange('displayName')}
              />
            </div>
          </div>

          {/* description */}
          <div className="space-y-1.5">
            <Label htmlFor="basics-description">Beschreibung</Label>
            <Textarea
              id="basics-description"
              placeholder="Kurze Beschreibung für das Kundenportal…"
              value={form.description}
              onChange={handleChange('description')}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* category */}
            <div className="space-y-1.5">
              <Label htmlFor="basics-category">Kategorie</Label>
              <Input
                id="basics-category"
                placeholder="z.B. CRM, Produktivität"
                value={form.category}
                onChange={handleChange('category')}
              />
            </div>

            {/* mcpUrl */}
            <div className="space-y-1.5">
              <Label htmlFor="basics-mcp-url">
                MCP-URL
              </Label>
              <Input
                id="basics-mcp-url"
                type="url"
                placeholder={`Aktuell: (gespeichert) — neu eingeben zum Ändern`}
                value={form.mcpUrl}
                onChange={handleChange('mcpUrl')}
                aria-invalid={!!fieldErrors.mcpUrl}
                aria-describedby={fieldErrors.mcpUrl ? 'basics-mcp-url-error' : undefined}
              />
              {fieldErrors.mcpUrl && (
                <p id="basics-mcp-url-error" role="alert" className="text-xs text-red-600">
                  {fieldErrors.mcpUrl}
                </p>
              )}
              <p className="text-xs text-gray-400">
                Leer lassen um die bestehende URL beizubehalten.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Icon Upload */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Icon</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              PNG, JPEG oder WebP. Maximal 500 KB. Empfohlene Größe: 128 × 128 px.
            </p>
          </div>
          <IconUploadField
            integrationId={integration.id}
            currentIconUrl={form.iconUrl || undefined}
            onUploaded={(newUrl) => {
              setForm((prev) => ({ ...prev, iconUrl: newUrl }));
              onSaved();
            }}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={isLoading} aria-label="Änderungen speichern">
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
          Speichern
        </Button>
      </div>
    </form>
  );
}
